// Catálogo CANÓNICO de bancos chilenos (código de plaza SBIF de 3 dígitos) para el pago
// a repartidores. Es un ESPEJO de `CHILE_BANKS` en el microservicio billing
// (`src/api/driver-payment/bank-codes.util.ts`): los pares (code, name) deben coincidir
// exactamente. Cualquier cambio acá debe replicarse allá (y viceversa).
//
// `name` es el nombre que los formularios de repartidor deben guardar en `bankData.bank`.
// El backend traduce ese nombre a código con `resolveBankCode`; acá solo se guarda el
// nombre canónico, nunca el código (el subdocumento lo escribe microservice-users).
//
// Códigos verificados contra el catálogo público (SBIF/CMF) al 2026-09. Quedan fuera los
// emisores sin código confirmado (Tanner Banco Digital, Fpay, Prepago Los Héroes, Caja
// Los Andes) — un repartidor con uno de ellos cae en "excluidos" de la planilla.

export const CHILE_BANKS = [
  { code: '001', name: 'Banco de Chile' },
  { code: '009', name: 'Banco Internacional' },
  { code: '012', name: 'BancoEstado' },
  { code: '014', name: 'Scotiabank Chile' },
  { code: '016', name: 'Banco de Crédito e Inversiones (BCI)' },
  { code: '028', name: 'Banco BICE' },
  { code: '031', name: 'HSBC Bank (Chile)' },
  { code: '037', name: 'Banco Santander' },
  { code: '039', name: 'Banco Itaú Chile' },
  { code: '049', name: 'Banco Security' },
  { code: '051', name: 'Banco Falabella' },
  { code: '053', name: 'Banco Ripley' },
  { code: '055', name: 'Banco Consorcio' },
  { code: '059', name: 'Banco BTG Pactual Chile' },
  // Cooperativas y emisores de prepago: no son bancos, pero reciben transferencias con
  // código propio y el registro los ofrece como opción.
  { code: '150', name: 'MACH' },
  { code: '672', name: 'Coopeuch' },
  { code: '726', name: 'Oriencoop' },
  { code: '730', name: 'Tenpo' },
  { code: '875', name: 'Mercado Pago' },
];

export const CHILE_BANK_NAMES = CHILE_BANKS.map((b) => b.name);

// Aliases con que los catálogos anteriores del front escribían el mismo banco, para
// preseleccionar el nombre canónico al EDITAR un repartidor ya guardado con un valor
// viejo ("Santander" → "Banco Santander", slug "santander" → "Banco Santander").
// Misma tolerancia que `bankNameKey` del backend (minúsculas, sin tildes ni puntuación).
const ALIAS_TO_NAME = {
  'bancochile': 'Banco de Chile',
  'banco_chile': 'Banco de Chile',
  'bancoedwards': 'Banco de Chile',
  'bancointernacional': 'Banco Internacional',
  'internacional': 'Banco Internacional',
  'bancoestado': 'BancoEstado',
  'bancoestadochile': 'BancoEstado',
  'banco_estado': 'BancoEstado',
  'scotiabank': 'Scotiabank Chile',
  'scotiabankchile': 'Scotiabank Chile',
  'bci': 'Banco de Crédito e Inversiones (BCI)',
  'bancobci': 'Banco de Crédito e Inversiones (BCI)',
  'bancodecredito': 'Banco de Crédito e Inversiones (BCI)',
  'bice': 'Banco BICE',
  'bancobice': 'Banco BICE',
  'hsbc': 'HSBC Bank (Chile)',
  'santander': 'Banco Santander',
  'bancosantander': 'Banco Santander',
  'itau': 'Banco Itaú Chile',
  'itauchile': 'Banco Itaú Chile',
  'security': 'Banco Security',
  'bancosecurity': 'Banco Security',
  'falabella': 'Banco Falabella',
  'bancoconsorcio': 'Banco Consorcio',
  'bancofalabella': 'Banco Falabella',
  'ripley': 'Banco Ripley',
  'bancoripley': 'Banco Ripley',
  'consorcio': 'Banco Consorcio',
  'btgpactual': 'Banco BTG Pactual Chile',
  'mach': 'MACH',
  'coopeuch': 'Coopeuch',
  'oriencoop': 'Oriencoop',
  'tenpo': 'Tenpo',
  'mercadopago': 'Mercado Pago',
};

/** Normaliza un nombre a su clave de búsqueda (minúsculas, sin tildes ni puntuación). */
const nameKey = (raw) =>
  String(raw ?? '')
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const NAME_BY_KEY = new Map(CHILE_BANKS.map((b) => [nameKey(b.name), b.name]));

/** Nombre canónico para un código de plaza (3 dígitos). `null` si no existe. */
export function chileBankByCode(code) {
  const c = String(code ?? '').trim();
  return CHILE_BANKS.find((b) => b.code === c)?.name ?? null;
}

/**
 * Nombre canónico a partir de lo que haya guardado el repartidor: si ya es un nombre
 * canónico lo devuelve; si es un alias/slug viejo lo mapea. `undefined` si no se
 * reconoce (el llamador deja el select vacío).
 */
export function resolveChileBankName(raw) {
  const key = nameKey(raw);
  if (!key) return undefined;
  if (NAME_BY_KEY.has(key)) return NAME_BY_KEY.get(key);
  return ALIAS_TO_NAME[key];
}
